import type { PropsWithChildren } from "react"
import React, { createContext, useContext } from "react"

import type { CurrentUserFieldsFragment } from "~graphql/users.generated"
import { useUser as useApiUser } from "~utils/hooks/use-user"

interface IUserContext {
    user: CurrentUserFieldsFragment | null | undefined
    isAdmin: boolean
    isDeveloper: boolean
    isLoading: boolean
    isImpersonating: boolean
    isAdminNotImpersonating: boolean
    error: Error | undefined
}

export const UserContext = createContext<IUserContext>({
    user: null,
    isAdmin: false,
    isDeveloper: false,
    isLoading: false,
    isImpersonating: false,
    isAdminNotImpersonating: false,
    error: undefined,
})

export function UserProvider({ children }: Readonly<PropsWithChildren>) {
    const value = useUserValues()

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export function useUser(): IUserContext {
    return useContext(UserContext)
}

// Not intended for use outside of UserProvider.
function useUserValues(): IUserContext {
    const { isLoading, user, error } = useApiUser()

    const isAdmin = Boolean(user?.is_admin)
    const isDeveloper = Boolean(user?.is_developer)
    const isImpersonating = Boolean(
        user?.impersonation_details?.is_impersonated
    )

    const isAdminNotImpersonating = isAdmin && !isImpersonating

    return {
        user,
        isAdmin,
        isDeveloper,
        isLoading,
        error,
        isImpersonating,
        isAdminNotImpersonating,
    }
}
